import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ExternalLink, Newspaper, Quote, Star, Filter } from 'lucide-react';
import Header from '../components/Header';
import { useSEO } from '../utils/seo';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = [
  { code: '', label: 'All Markets' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'CA', label: 'Canada' },
  { code: 'GLOBAL', label: 'Global' },
];

const InTheNewsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('');

  useSEO({
    title: 'Shopperden in the News — Press Coverage & Mentions',
    description: 'Press coverage, interviews, and data citations featuring Shopperden across UK, India and Canada real estate publications.',
    canonical: 'https://shopperden.com/in-the-news',
    image: 'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=1200&h=630&fit=crop&auto=format',
    type: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Shopperden in the News',
      description: 'Press coverage and citations featuring Shopperden',
      url: 'https://shopperden.com/in-the-news',
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/press-mentions`, {
          params: country ? { country } : {},
        });
        if (!cancelled) setItems(r.data?.items || []);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [country]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-12" data-testid="press-room-hero">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] font-semibold mb-3">Press Room</p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#0F172A] mb-4">
            Shopperden <em className="font-normal text-[#E07A3A]">in the News</em>
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Press coverage, interviews, and data citations featuring Shopperden across UK, India and Canada property publications.
          </p>
          <div className="flex justify-center mt-6">
            <ShareButtons
              url="https://shopperden.com/in-the-news"
              title="Shopperden in the News"
              size="sm"
              label="Share"
              dataTestId="press-share"
            />
          </div>
        </div>

        {/* Country filter */}
        <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="press-filters">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold flex items-center gap-1.5">
            <Filter className="h-3 w-3" /> Market:
          </span>
          {COUNTRIES.map((c) => (
            <button
              key={c.code || 'all'}
              onClick={() => setCountry(c.code)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors rounded ${
                country === c.code ? 'bg-[#E07A3A] text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-[#E07A3A] hover:text-[#E07A3A]'
              }`}
              data-testid={`press-filter-${c.code || 'all'}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Mentions list */}
        {loading ? (
          <div className="text-center py-20 text-slate-400" data-testid="press-loading">Loading press mentions…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded p-12 text-center" data-testid="press-empty">
            <Newspaper className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">No press mentions yet for this market</p>
            <p className="text-slate-400 text-sm">Coverage rolls in as our pitches land. Check back soon.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((m) => (
              <a
                key={m.id || m.source_url}
                href={m.source_url}
                target="_blank"
                rel="noopener nofollow"
                className="group bg-white border border-slate-200 rounded p-5 sm:p-6 hover:border-[#E07A3A] hover:shadow-sm transition-all flex flex-col sm:flex-row gap-5"
                data-testid={`press-mention-${m.id || ''}`}
              >
                {/* Logo / Publication block */}
                <div className="flex-shrink-0 w-full sm:w-44 flex flex-col items-center sm:items-start">
                  {m.source_logo ? (
                    <img
                      src={m.source_logo}
                      alt={m.source_name}
                      loading="lazy"
                      className="h-10 max-w-[160px] object-contain object-left grayscale group-hover:grayscale-0 transition-all"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <Newspaper className="h-8 w-8 text-slate-300" />
                  )}
                  <p className="text-xs text-slate-500 mt-2 font-semibold">{m.source_name}</p>
                  {m.published_on && (
                    <p className="text-[11px] text-slate-400">
                      {new Date(m.published_on).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {m.featured && (
                      <span className="inline-flex items-center gap-1 bg-[#FEF3C7] text-[#A16207] text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded">
                        <Star className="h-3 w-3" /> Featured
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                      {(m.category || 'feature').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg sm:text-xl text-[#0F172A] mb-2 leading-tight group-hover:text-[#E07A3A] transition-colors">
                    {m.title}
                  </h3>
                  {m.snippet && (
                    <p className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-slate-200 pl-3">
                      <Quote className="h-3 w-3 inline-block mr-1 text-slate-300" />
                      {m.snippet}
                    </p>
                  )}
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#E07A3A] mt-3 font-semibold flex items-center gap-1.5">
                    Read on {m.source_name}
                    <ExternalLink className="h-3 w-3" />
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Pitch CTA */}
        <div className="bg-[#0F172A] text-white p-8 sm:p-10 rounded mt-12 text-center" data-testid="press-pitch-cta">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] mb-3 font-semibold">Journalists & Editors</p>
          <h2 className="font-['Playfair_Display'] text-2xl mb-3">Need a quote on property tech?</h2>
          <p className="text-white/70 mb-6 text-sm max-w-xl mx-auto">
            We respond within 4 hours on UK / IN / CA real estate market data, NRI trends, AI matchmaking, or yield analytics.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#E07A3A] hover:bg-[#C4652E] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold rounded"
            data-testid="press-contact-cta"
          >
            Contact press team
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InTheNewsPage;
